import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  Component,
} from "react";
import { Player } from "@remotion/player";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import ClosedCaptionDisabledIcon from "@mui/icons-material/ClosedCaptionDisabled";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { PanelLeft } from "lucide-react";
import { VideoEditorComposition } from "./remotionComposition";
import VideoEditorLayerOverlay from "./VideoEditorLayerOverlay";
import VideoEditorSubtitleOverlay from "./VideoEditorSubtitleOverlay";
import {
  selectVideoEditorCompositionSize,
  selectVideoEditorDuration,
  selectVideoEditorEdl,
  selectVideoEditorPlayhead,
  selectVideoEditorSelectedClipId,
} from "./videoEditorSelectors";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  applyVideoEditorEdl,
  setVideoEditorSelectedClip,
} from "../../actions/videoEditor";
import { applySourceSizesToEdl } from "./clipProperties";
import { DEFAULT_SUBTITLE_OPTIONS } from "../../components/Modal/ModalCreateWithIA/subtitlePresets";
import { useTranslationApp } from "../../lib/i18next";
import {
  MODAL_SUBTITLE_OPTIONS_PERSONALIZE,
  MODAL_VIDEO_EDITOR_CAPTIONS_ARIA,
  MODAL_VIDEO_EDITOR_CAPTIONS_DISABLE,
  MODAL_VIDEO_EDITOR_CAPTIONS_ENABLE,
  MODAL_VIDEO_EDITOR_EMPTY_STAGE_HINT,
  MODAL_VIDEO_EDITOR_EMPTY_STAGE_TITLE,
  MODAL_VIDEO_EDITOR_PAUSE,
  MODAL_VIDEO_EDITOR_PLAY,
  MODAL_VIDEO_EDITOR_PROPERTIES_TITLE,
} from "../../i18n/keysTranslations";
import {
  formatVideoEditorClock,
  VIDEO_EDITOR_FPS,
  VIDEO_EDITOR_LABEL_COLOR,
  VIDEO_EDITOR_PLAYBACK_CONTROLS_HEIGHT,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { getEdlDuration } from "./projectSchema";
import { areEditorSubtitlesEnabled } from "./subtitleEdl";
import { useStageDimensions } from "./useStageDimensions";
import { useVideoEditorPlaybackClock } from "./videoEditorPlaybackClock";
import { resolvePlaybackTimelineTime } from "./playbackSync";

const CONTROL_BUTTON_SX = {
  width: 28,
  height: 28,
  p: 0,
  bgcolor: "#fff",
  border: "1px solid #e4e7ed",
  boxShadow: "0 3px 9px #a3aabd45",
  color: "#68768a",
  "&:hover": {
    bgcolor: "#faf9ff",
    borderColor: "#bdb4ff",
    color: "#5834ee",
  },
  "&.Mui-disabled": {
    bgcolor: "#fff",
    color: "#c5ceda",
    borderColor: "#e4e7ed",
  },
};

class VideoEditorPlayerBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <Box sx={{ width: "100%", height: "100%", bgcolor: "#fff" }} />;
    }

    return this.props.children;
  }
}

const VideoEditorPlayer = forwardRef(
  (
    {
      onPlayheadChange = () => {},
      onPlayingChange = () => {},
      isPlaying = false,
      onOpenSubtitleOptions = () => {},
      showPropertiesToggle = false,
      onOpenProperties = () => {},
    },
    ref
  ) => {
    const { t } = useTranslationApp();
    const dispatch = useDispatchApp();
    const edl = useSelectorApp(selectVideoEditorEdl);
    const duration = useSelectorApp(selectVideoEditorDuration);
    const playhead = useSelectorApp(selectVideoEditorPlayhead);
    const selectedClipId = useSelectorApp(selectVideoEditorSelectedClipId);
    const { width, height } = useSelectorApp(selectVideoEditorCompositionSize);
    const playerRef = useRef(null);
    const stageRef = useRef(null);
    const [captionsMenuAnchor, setCaptionsMenuAnchor] = useState(null);
    const stageHostRef = useRef(null);
    const stageDimensions = useStageDimensions({
      containerRef: stageHostRef,
      compositionWidth: width,
      compositionHeight: height,
    });
    const isSyncingFromTimelineRef = useRef(false);
    const playbackClock = useVideoEditorPlaybackClock();
    const edlRef = useRef(edl);
    edlRef.current = edl;
    const clockLabelRef = useRef(null);
    const fps = edl?.fps || VIDEO_EDITOR_FPS;
    const fpsRef = useRef(fps);
    fpsRef.current = fps;
    const durationInFrames = useMemo(
      () => Math.max(1, Math.ceil((duration || 1) * fps)),
      [duration, fps]
    );
    const hasClips = Boolean(Object.keys(edl?.clips?.byId || {}).length);

    const videoSourceSignature = Object.values(edl?.clips?.byId || {})
      .filter((clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO)
      .map(
        (clip) =>
          `${clip.id}:${clip.sourceUrl}:${clip.sourceWidth || 0}x${clip.sourceHeight || 0}`
      )
      .sort()
      .join("|");

    useEffect(() => {
      const root = stageRef.current;
      if (!root) {
        return undefined;
      }

      const syncSourceSizes = () => {
        const currentEdl = edlRef.current;
        if (!currentEdl) {
          return;
        }
        const sizesBySourceUrl = {};
        root
          .querySelectorAll("[data-video-editor-video-layer]")
          .forEach((layer) => {
            const clipId = layer.getAttribute("data-video-editor-video-layer");
            const clip = currentEdl.clips?.byId?.[clipId];
            const video = layer.querySelector("video");
            const videoWidth = Number(video?.videoWidth);
            const videoHeight = Number(video?.videoHeight);
            if (!clip?.sourceUrl || !(videoWidth > 0) || !(videoHeight > 0)) {
              return;
            }
            sizesBySourceUrl[clip.sourceUrl] = {
              width: videoWidth,
              height: videoHeight,
            };
          });
        if (!Object.keys(sizesBySourceUrl).length) {
          return;
        }
        const nextEdl = applySourceSizesToEdl({
          edl: currentEdl,
          sizesBySourceUrl,
        });
        if (nextEdl !== currentEdl) {
          dispatch(applyVideoEditorEdl({ edl: nextEdl }));
        }
      };

      syncSourceSizes();
      const videos = [...root.querySelectorAll("video")];
      videos.forEach((video) => {
        video.addEventListener("loadedmetadata", syncSourceSizes);
      });
      return () => {
        videos.forEach((video) => {
          video.removeEventListener("loadedmetadata", syncSourceSizes);
        });
      };
    }, [dispatch, videoSourceSignature, width, height]);
    const hasSubtitleClips = Object.values(edl?.clips?.byId || {}).some(
      (clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE
    );
    const subtitlesEnabled = areEditorSubtitlesEnabled(edl?.subtitleOptions);
    const resolvedDuration = duration || getEdlDuration({ edl }) || 0;

    const clips = edl?.clips;
    const compositionFps = edl?.fps;
    const compositionWidth = edl?.compositionWidth;
    const compositionHeight = edl?.compositionHeight;
    const mediaClipsByIdRef = useRef(clips?.byId);
    mediaClipsByIdRef.current = clips?.byId;
    const mediaClipsSignature = Object.values(clips?.byId || {})
      .filter(
        (clip) =>
          clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
          clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
      )
      .map(
        (clip) =>
          `${clip.id}:${clip.sourceUrl}:${clip.inPoint}:${clip.outPoint}:${clip.position}:${clip.playbackRate || 1}:${clip.volume ?? 1}:${clip.layerIndex || 0}:${clip.layout?.x ?? ""}:${clip.layout?.y ?? ""}:${clip.layout?.width ?? ""}:${clip.layout?.height ?? ""}`
      )
      .sort()
      .join("|");

    const inputProps = useMemo(() => {
      const mediaById = {};
      Object.values(mediaClipsByIdRef.current || {}).forEach((clip) => {
        if (
          clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
          clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
        ) {
          mediaById[clip.id] = clip;
        }
      });

      return {
        edl: {
          clips: { byId: mediaById },
          fps: compositionFps,
          compositionWidth,
          compositionHeight,
        },
      };
      // eslint-disable-next-line
    }, [
      compositionFps,
      compositionHeight,
      compositionWidth,
      mediaClipsSignature,
    ]);

    const alignCompositionToMedia = useCallback(() => {
      const player = playerRef.current;
      const fpsValue = fpsRef.current;
      if (!player) {
        return playbackClock.get();
      }

      const compositionTime = player.getCurrentFrame() / fpsValue;
      const timelineTime = resolvePlaybackTimelineTime({
        edl: edlRef.current,
        root: stageRef.current,
        compositionTime,
      });
      const nextTime = Math.max(compositionTime, timelineTime);
      if (nextTime - compositionTime > 1 / fpsValue) {
        isSyncingFromTimelineRef.current = true;
        player.seekTo(Math.floor(nextTime * fpsValue));
        window.setTimeout(() => {
          isSyncingFromTimelineRef.current = false;
        }, 80);
      }
      playbackClock.set(nextTime);
      return nextTime;
    }, [playbackClock]);

    const pausePlayback = useCallback(() => {
      const player = playerRef.current;
      if (!player) return;
      alignCompositionToMedia();
      player.pause();
    }, [alignCompositionToMedia]);

    const onPauseForEdit = useCallback(() => {
      pausePlayback();
    }, [pausePlayback]);

    const onTogglePlaying = useCallback(() => {
      const player = playerRef.current;
      if (!player) return;
      if (player.isPlaying()) {
        pausePlayback();
        return;
      }
      player.play();
    }, [pausePlayback]);

    const onCloseCaptionsMenu = useCallback(() => {
      setCaptionsMenuAnchor(null);
    }, []);

    const onToggleSubtitlesEnabled = useCallback(() => {
      if (!edl) {
        return;
      }
      const nextEnabled = !areEditorSubtitlesEnabled(edl.subtitleOptions);
      dispatch(
        applyVideoEditorEdl({
          edl: {
            ...edl,
            subtitleOptions: {
              ...(edl.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS),
              enabled: nextEnabled,
            },
          },
        })
      );
      if (!nextEnabled) {
        const selectedClip = edl.clips?.byId?.[selectedClipId];
        if (selectedClip?.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE) {
          dispatch(setVideoEditorSelectedClip({ clipId: null }));
        }
      }
      setCaptionsMenuAnchor(null);
    }, [dispatch, edl, selectedClipId]);

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          playerRef.current?.play();
        },
        pause: () => {
          pausePlayback();
        },
        togglePlaying: () => {
          const player = playerRef.current;
          if (!player) return;
          if (player.isPlaying()) {
            pausePlayback();
            return;
          }
          player.play();
        },
        isPlaying: () => Boolean(playerRef.current?.isPlaying()),
      }),
      [pausePlayback]
    );

    useEffect(() => {
      const player = playerRef.current;
      if (!player || !edl) return undefined;

      const onFrameUpdate = ({ detail }) => {
        if (isSyncingFromTimelineRef.current) return;
        if (player.isPlaying()) return;
        playbackClock.set(detail.frame / fps);
      };

      const onPlay = () => onPlayingChange({ isPlaying: true });
      const onPause = () => {
        onPlayheadChange({ playhead: playbackClock.get() });
        onPlayingChange({ isPlaying: false });
      };
      const onEnded = () => {
        onPlayheadChange({ playhead: playbackClock.get() });
        onPlayingChange({ isPlaying: false });
      };

      player.addEventListener("frameupdate", onFrameUpdate);
      player.addEventListener("play", onPlay);
      player.addEventListener("pause", onPause);
      player.addEventListener("ended", onEnded);

      return () => {
        player.removeEventListener("frameupdate", onFrameUpdate);
        player.removeEventListener("play", onPlay);
        player.removeEventListener("pause", onPause);
        player.removeEventListener("ended", onEnded);
      };
    }, [edl, fps, onPlayheadChange, onPlayingChange, playbackClock]);

    useEffect(() => {
      if (isPlaying) {
        return undefined;
      }
      playbackClock.set(playhead);
      return undefined;
    }, [isPlaying, playhead, playbackClock]);

    useEffect(() => {
      if (!isPlaying) {
        return undefined;
      }

      let rafId = 0;
      const tick = () => {
        const player = playerRef.current;
        const fpsValue = fpsRef.current;
        const compositionTime = player
          ? player.getCurrentFrame() / fpsValue
          : playbackClock.get();
        const nextTime = resolvePlaybackTimelineTime({
          edl: edlRef.current,
          root: stageRef.current,
          compositionTime,
        });
        playbackClock.set(nextTime);
        if (clockLabelRef.current) {
          clockLabelRef.current.textContent = formatVideoEditorClock(nextTime);
        }
        rafId = window.requestAnimationFrame(tick);
      };

      rafId = window.requestAnimationFrame(tick);
      return () => window.cancelAnimationFrame(rafId);
    }, [isPlaying, playbackClock]);

    useEffect(() => {
      if (isPlaying) {
        return undefined;
      }

      const syncLabel = (time) => {
        if (clockLabelRef.current) {
          clockLabelRef.current.textContent = formatVideoEditorClock(time);
        }
      };

      syncLabel(playbackClock.get() ?? playhead);
      return playbackClock.subscribe(syncLabel);
    }, [isPlaying, playhead, playbackClock]);

    useEffect(() => {
      const player = playerRef.current;
      if (!player || !edl) return undefined;

      const targetFrame = Math.min(
        durationInFrames - 1,
        Math.max(0, Math.floor(playhead * fps))
      );
      if (Math.abs(player.getCurrentFrame() - targetFrame) <= 1) {
        return undefined;
      }

      isSyncingFromTimelineRef.current = true;
      player.seekTo(targetFrame);
      const timeoutId = window.setTimeout(() => {
        isSyncingFromTimelineRef.current = false;
      }, 80);

      return () => {
        window.clearTimeout(timeoutId);
        isSyncingFromTimelineRef.current = false;
      };
    }, [durationInFrames, edl, fps, playhead]);

    if (!edl) {
      return null;
    }

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#eceff3",
          px: 1,
          pt: 1,
          pb: 0,
          position: "relative",
        }}
      >
        {showPropertiesToggle ? (
          <IconButton
            aria-label={t(MODAL_VIDEO_EDITOR_PROPERTIES_TITLE)}
            onClick={onOpenProperties}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 8,
              width: 36,
              height: 36,
              color: "text.secondary",
              bgcolor: "background.paper",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <PanelLeft size={18} color="currentColor" />
          </IconButton>
        ) : null}
        <Box
          ref={stageHostRef}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            ref={stageRef}
            sx={{
              width: stageDimensions.width || undefined,
              height: stageDimensions.height || undefined,
              position: "relative",
              overflow: "visible",
              flexShrink: 0,
              bgcolor: "#fff",
            }}
          >
            {hasClips && resolvedDuration > 0 ? (
              <VideoEditorPlayerBoundary>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    pointerEvents: isPlaying ? "auto" : "none",
                  }}
                >
                  <Player
                    ref={playerRef}
                    component={VideoEditorComposition}
                    inputProps={inputProps}
                    durationInFrames={durationInFrames}
                    compositionWidth={width}
                    compositionHeight={height}
                    fps={fps}
                    clickToPlay={false}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      backgroundColor: "#fff",
                    }}
                    acknowledgeRemotionLicense
                    numberOfSharedAudioTags={Math.max(
                      5,
                      Object.values(edl.clips?.byId || {}).filter(
                        (clip) =>
                          clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
                          clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
                      ).length
                    )}
                  />
                </Box>
                <VideoEditorLayerOverlay
                  stageRef={stageRef}
                  isPlaying={isPlaying}
                  onPauseForEdit={onPauseForEdit}
                />
                <VideoEditorSubtitleOverlay
                  stageRef={stageRef}
                  isPlaying={isPlaying}
                  onPauseForEdit={onPauseForEdit}
                />
              </VideoEditorPlayerBoundary>
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.25,
                  color: "#a4adbb",
                  px: 2,
                  textAlign: "center",
                }}
              >
                <VideocamOutlinedIcon sx={{ fontSize: 32, color: "#a4adbb" }} />
                <Typography
                  sx={{ color: "#778498", fontSize: 13, fontWeight: 500 }}
                >
                  {t(MODAL_VIDEO_EDITOR_EMPTY_STAGE_TITLE)}
                </Typography>
                <Typography sx={{ color: "#a6afbc", fontSize: 11 }}>
                  {t(MODAL_VIDEO_EDITOR_EMPTY_STAGE_HINT)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            height: VIDEO_EDITOR_PLAYBACK_CONTROLS_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
            }}
          >
            <Typography
              ref={clockLabelRef}
              sx={{
                minWidth: 36,
                textAlign: "right",
                color: VIDEO_EDITOR_LABEL_COLOR,
                fontSize: 12,
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1,
              }}
            >
              {formatVideoEditorClock(playhead)}
            </Typography>
            <IconButton
              aria-label={t(
                isPlaying ? MODAL_VIDEO_EDITOR_PAUSE : MODAL_VIDEO_EDITOR_PLAY
              )}
              onClick={onTogglePlaying}
              disabled={!hasClips || resolvedDuration <= 0}
              sx={CONTROL_BUTTON_SX}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: 16 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 16, ml: "1px" }} />
              )}
            </IconButton>
            <Typography
              sx={{
                minWidth: 36,
                textAlign: "left",
                color: VIDEO_EDITOR_LABEL_COLOR,
                fontSize: 12,
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1,
              }}
            >
              {formatVideoEditorClock(resolvedDuration)}
            </Typography>
          </Box>
          {hasSubtitleClips ? (
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton
                aria-label={t(MODAL_VIDEO_EDITOR_CAPTIONS_ARIA)}
                aria-haspopup="true"
                aria-expanded={Boolean(captionsMenuAnchor)}
                onClick={(event) => setCaptionsMenuAnchor(event.currentTarget)}
                sx={CONTROL_BUTTON_SX}
              >
                {subtitlesEnabled ? (
                  <ClosedCaptionIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ClosedCaptionDisabledIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
              <Menu
                anchorEl={captionsMenuAnchor}
                open={Boolean(captionsMenuAnchor)}
                onClose={onCloseCaptionsMenu}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    onCloseCaptionsMenu();
                    onOpenSubtitleOptions();
                  }}
                >
                  {t(MODAL_SUBTITLE_OPTIONS_PERSONALIZE)}
                </MenuItem>
                <MenuItem onClick={onToggleSubtitlesEnabled}>
                  {t(
                    subtitlesEnabled
                      ? MODAL_VIDEO_EDITOR_CAPTIONS_DISABLE
                      : MODAL_VIDEO_EDITOR_CAPTIONS_ENABLE
                  )}
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }
);

VideoEditorPlayer.displayName = "VideoEditorPlayer";

export default VideoEditorPlayer;
